import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '../utils/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
  role?: string;
  permissions?: string[];
  tenantId?: string;
  subdomain?: string;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value);

  function loadAuthState() {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser);
      } catch {
        user.value = null;
      }
    }
  }

  function saveAuthState(userData: User) {
    localStorage.setItem('user', JSON.stringify(userData));
    user.value = userData;
  }

  function clearAuthState() {
    localStorage.removeItem('user');
    user.value = null;
    error.value = null;
  }

  async function login(loginId: string, password: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.login({ login: loginId, password });
      const userData: User = response.user ?? response;

      if (!userData.subdomain && typeof window !== 'undefined') {
        const storedSubdomain = localStorage.getItem('currentSubdomain');
        if (storedSubdomain) {
          userData.subdomain = storedSubdomain;
        } else {
          const hostname = window.location.hostname;
          const match = hostname.match(/^([^.]+)\./);
          if (match && match[1] !== 'localhost' && match[1] !== 'www') {
            userData.subdomain = match[1];
          }
        }
      }

      saveAuthState(userData);
      return userData;
    } catch (err: any) {
      error.value =
        err.response?.data?.message ||
        err.message ||
        'Login failed. Please check your credentials.';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    isLoading.value = true;
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearAuthState();
      isLoading.value = false;
    }
  }

  async function refreshAccessToken() {
    try {
      await authApi.refreshToken();
    } catch (err) {
      clearAuthState();
      throw err;
    }
  }

  loadAuthState();

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    login,
    logout,
    refreshAccessToken,
    clearAuthState,
    loadAuthState,
  };
});
